import { money } from '../lib/format';
import type { BookingQuote } from '../lib/types';

/**
 * The priced quote, itemised. Showing every line (and not just a total) is
 * what makes a surcharge feel like a published rule rather than a surprise.
 */
export function QuoteTable({ quote, compact = false }: { quote: BookingQuote; compact?: boolean }) {
  return (
    <div>
      <dl>
        {quote.lineItems.map((item, index) => (
          <div
            key={`${item.kind}-${index}`}
            className="flex items-baseline justify-between gap-4 border-b border-[color:var(--color-rule)] py-2"
          >
            <dt className="text-sm">
              {item.label}
              {item.quantity > 1 && (
                <span className="ml-2 font-mono text-xs text-[color:var(--color-ink-faint)]">
                  {item.quantity} x {money(item.unitPrice)}
                </span>
              )}
            </dt>
            <dd className="typed shrink-0">{money(item.amount)}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-3 flex items-baseline justify-between gap-4">
        <span className="font-display text-lg font-bold">Total</span>
        <span className="font-display text-2xl font-extrabold">{money(quote.total)}</span>
      </div>

      {quote.depositDue > 0 && (
        <dl className="mt-3 border-t border-[color:var(--color-rule)] pt-3">
          <div className="flex justify-between gap-4 text-sm">
            <dt>Deposit to hold the date</dt>
            <dd className="typed">{money(quote.depositDue)}</dd>
          </div>
          <div className="flex justify-between gap-4 text-sm">
            <dt>Balance{quote.balanceDueDate && ` by ${quote.balanceDueDate}`}</dt>
            <dd className="typed">{money(quote.balanceDue)}</dd>
          </div>
        </dl>
      )}

      {!compact && quote.notes.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-sm text-[color:var(--color-ink-soft)]">
          {quote.notes.map((note) => (
            <li key={note} className="flex gap-2">
              <span aria-hidden="true" className="text-[color:var(--color-stamp)]">
                &bull;
              </span>
              {note}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
